import { Controller } from '@hotwired/stimulus'
import { composeNote, editedRows, writtenSentences } from '../lib/review_note'

export default class extends Controller {
  static values = { detailChoices: { type: Array, default: ["correct", "supplier"] }, note: Boolean, draftKey: String }

  static targets = ['row', 'progress', 'ticks', 'accept', 'filter', 'remainingCount', 'count', 'bulk', 'apply', 'bulkBar', 'bulkControls', 'undoStrip', 'undoCount', 'all', 'search', 'empty', 'meter', 'shown']

  connect() {
    this.validate = this.validate.bind(this)
    this.form = this.element.closest('form')
    this.form.addEventListener('submit', this.validate)
    // A value review writes the answers into the note (ADR-015).
    this.noteField = this.noteValue ? this.form.querySelector('[name="task_action[resolution_notes]"]') : null
    if (this.noteField) {
      this.noteOwned = this.noteField.value.trim() !== ''
      this.noteEdited = () => { this.noteOwned = true; this.saveDraft() }
      this.noteField.addEventListener('input', this.noteEdited)
      // A note that arrives written (a re-rendered form) already says the rows as they stand.
      if (this.noteOwned) this.noteWritten = writtenSentences(this.noteRows())
      this.restoreDraft()
    }
    this.refresh()
    this.initialValues = this.valuesSignature()
    this.warnBeforeLeave = event => {
      if (!this.submitting && this.initialValues !== this.valuesSignature()) {
        event.preventDefault()
        event.returnValue = ''
      }
    }
    window.addEventListener('beforeunload', this.warnBeforeLeave)
    this.confirmNavigation = event => {
      if (!this.submitting && this.initialValues !== this.valuesSignature() && !window.confirm('Leave this review? Your unsubmitted decisions will be lost.')) event.preventDefault()
    }
    document.addEventListener('turbo:before-visit', this.confirmNavigation)
    // A value review whose columns do not fit its card stacks each row instead of clipping it.
    if (this.element.classList.contains('review-values') && typeof ResizeObserver !== 'undefined') {
      // Measured on the next frame, so the stacked rows' own height change
      // never re-enters the observer callback, and again once web fonts load.
      this.fitObserver = new ResizeObserver(() => {
        cancelAnimationFrame(this.fitFrame)
        this.fitFrame = requestAnimationFrame(() => this.fitValues())
      })
      this.fitObserver.observe(this.element)
      this.fitValues()
      document.fonts?.ready?.then(() => this.fitValues())
    }
  }

  // The source columns take their values' whole width (prices never wrap), so a
  // long value ("$61.40 USD/unit (PO cost)") or a narrow card can push the row
  // past the card's edge, where the card would cut it off. Then each row stacks:
  // the item, then each source's value on its own line. It unstacks once the
  // card is as wide as the columns needed.
  fitValues() {
    const list = this.element.querySelector('.collection-review__list')
    if (!list) return
    if (this.element.hasAttribute('data-stack')) {
      if (this.element.clientWidth >= (this.stackedAt || Infinity)) {
        this.element.removeAttribute('data-stack')
        this.fitValues()
      }
      return
    }
    if (list.scrollWidth > list.clientWidth + 1) {
      this.stackedAt = this.element.clientWidth + (list.scrollWidth - list.clientWidth)
      this.element.setAttribute('data-stack', '')
    }
  }

  valuesSignature() {
    return JSON.stringify(Array.from(this.element.querySelectorAll('[data-choice], [data-detail]')).map(field => field.value))
  }

  disconnect() {
    this.form.removeEventListener('submit', this.validate)
    if (this.noteField) this.noteField.removeEventListener('input', this.noteEdited)
    window.removeEventListener('beforeunload', this.warnBeforeLeave)
    document.removeEventListener('turbo:before-visit', this.confirmNavigation)
    this.fitObserver?.disconnect()
    cancelAnimationFrame(this.fitFrame)
  }

  complete(row) {
    const choice = row.querySelector('[data-choice]').value
    return !!choice && (!this.detailChoicesValue.includes(choice) || !!row.querySelector('[data-detail]').value.trim())
  }

  refresh(event) {
    if (this.hasAllTarget && event?.target === this.allTarget) return
    if (event?.target.matches('[data-choice]')) {
      event.target.closest('[data-collection-review-target="row"]').querySelector('[data-detail]').value = ''
    }
    let selected = 0
    this.rowTargets.forEach(row => {
      const choice = row.querySelector('[data-choice]')
      ;(row.querySelector('[data-combo-input]') || choice).setCustomValidity('')
      row.querySelector('[data-detail]').setCustomValidity('')
      const fromValue = (this.pickedRadio(row)?.dataset || {}).pickDetail !== undefined
      row.querySelector('[data-detail-label]').hidden = fromValue || !this.detailChoicesValue.includes(choice.value)
      // "Remember for <supplier>" belongs to a matched answer only.
      const remember = row.querySelector('[data-remember]')
      if (remember) remember.hidden = !choice.value.startsWith('match:')
      // A short review's visible choice follows the hidden field, whoever set it.
      row.querySelectorAll('[data-pick]').forEach(radio => { radio.checked = this.radioMatches(row, radio); radio.setCustomValidity('') })
      const check = row.querySelector('[data-check]')
      if (check) check.checked = choice.value === 'reviewed'
      // In the table, the words an answer needs sit right under that answer.
      const detail = row.querySelector('[data-detail-label]')
      const picked = row.querySelector('[data-pick]:checked')?.closest('.review-list__choice')
      if (picked && !detail.hidden && row.closest('.review-list--table') && picked.nextElementSibling !== detail) picked.after(detail)
      const state = !choice.value ? null : !this.complete(row) ? 'incomplete' : ['supplier', 'uncertain', 'no_match'].includes(choice.value) ? 'followup' : 'reviewed'
      // Only a follow-up gets a tape; a plain decision already reads in its menu,
      // and a decision that still needs words shows its open field instead.
      const [words, tone] = { followup: ['Follow-up', 'wait'] }[state] || ['', '']
      const tape = row.querySelector('[data-status]')
      tape.textContent = words
      tape.dataset.tone = tone
      tape.hidden = !words
      // A decided row takes the done wash, so what is left stands out.
      row.classList.toggle('is-decided', this.complete(row))
      if (row.querySelector('[data-select]')?.checked) selected++
    })
    const done = this.rowTargets.filter(row => this.complete(row)).length
    const total = this.rowTargets.length
    if (this.hasRemainingCountTarget) this.remainingCountTarget.textContent = total - done
    const words = done === total && total > 1 ? `${total === 2 ? 'Both' : `All ${total}`} reviewed` : `${done} of ${total} reviewed`
    this.progressTargets.forEach(target => { target.textContent = words })
    // Short ticks for the rows, filled as they are decided; none for one row or a long review.
    const ticks = total > 1 && total <= 12 ? Array.from({ length: total }, (_, index) => `<i${index < done ? ' class="is-on"' : ''}></i>`).join('') : ''
    this.ticksTargets.forEach(target => { target.innerHTML = ticks })
    // "Accept N suggestions" counts the undecided rows the agent made a suggestion for; one is a click away, so it hides.
    const open = this.rowTargets.filter(row => row.hasAttribute('data-suggested') && !row.querySelector('[data-choice]').value).length
    this.acceptTargets.forEach(button => { button.textContent = `Accept ${open} suggestions`; button.hidden = open < 2 })
    this.element.dataset.complete = done === total
    if (this.noteField) {
      const rows = this.noteRows()
      this.noteField.value = composeNote(this.noteField.value, rows, this.noteOwned, this.noteWritten)
      this.noteWritten = writtenSentences(rows)
      // The note grows with its text (autogrow mirrors it on its wrapper).
      const grow = this.noteField.closest('.grow-wrap')
      if (grow) grow.dataset.replicatedValue = this.noteField.value
      this.saveDraft()
    }
    if (this.hasMeterTarget) this.meterTarget.style.width = `${done / total * 100}%`
    const visible = this.rowTargets.filter(row => !row.hidden)
    // A short review has no bulk tools: no boxes, no bar.
    if (this.hasBulkBarTarget) {
      this.countTarget.textContent = selected
      this.applyTarget.disabled = selected === 0
      this.bulkControlsTarget.hidden = selected === 0
      this.bulkBarTarget.hidden = selected === 0 && !this.undoState
      const checked = visible.filter(row => row.querySelector('[data-select]').checked).length
      this.allTarget.checked = visible.length > 0 && checked === visible.length
      this.allTarget.indeterminate = checked > 0 && checked < visible.length
    }
    this.emptyTarget.hidden = visible.length > 0
    // With many rows, say how many the filter or search left in view.
    this.shownTarget.textContent = visible.length < this.rowTargets.length ? `· ${visible.length} shown` : ''
  }

  filter() {
    this.rowTargets.forEach(row => {
      row.hidden = (this.remainingOnly && this.complete(row)) || !row.dataset.searchText.includes(this.query)
      const box = row.querySelector('[data-select]')
      if (row.hidden && box) box.checked = false
    })
    this.refresh()
  }

  // A short review has neither tool: nothing is hidden and nothing is searched.
  get remainingOnly() {
    return this.hasFilterTarget && this.filterTarget.checked
  }

  get query() {
    return this.hasSearchTarget ? this.searchTarget.value.trim().toLowerCase() : ''
  }

  // A search box inside the form: Enter never submits the review; Escape clears the search.
  searchKey(event) {
    if (event.key === 'Enter') event.preventDefault()
    if (event.key === 'Escape' && event.target.value) {
      event.preventDefault()
      event.target.value = ''
      event.target.dispatchEvent(new Event('input', { bubbles: true }))
    }
  }

  // Clicking the item text selects its row, unless the click landed on a control.
  toggleRow(event) {
    if (event.target.closest('a, button, input, select, label, details')) return
    const box = event.currentTarget.closest('[data-collection-review-target="row"]').querySelector('[data-select]')
    if (!box || box.classList.contains('collection-review__unavailable')) return
    box.checked = !box.checked
    this.refresh()
  }

  // The Match & map combobox: type to narrow the saved candidates, arrow to one,
  // Enter or click to pick. "No suitable match" always stays offered.
  comboOpen(event) {
    const combo = event.target.closest('[data-combo]')
    this.comboFilter({ target: combo.querySelector('[data-combo-input]') })
    combo.querySelector('[data-combo-list]').hidden = false
    combo.querySelector('[data-combo-input]').setAttribute('aria-expanded', 'true')
  }

  comboFilter(event) {
    const input = event.target
    const combo = input.closest('[data-combo]')
    const list = combo.querySelector('[data-combo-list]')
    const query = input.value.trim().toLowerCase()
    const hidden = combo.querySelector('[data-choice]')
    // Typing something other than the chosen label clears the choice until a pick is made.
    if (hidden.value && input.value !== this.comboLabel(combo, hidden.value)) { hidden.value = ''; hidden.dispatchEvent(new Event('change', { bubbles: true })) }
    let first = null
    list.querySelectorAll('[role="option"]').forEach(option => {
      option.hidden = query !== '' && option.dataset.value !== 'no_match' && !option.textContent.toLowerCase().includes(query)
      option.classList.remove('is-active')
      if (!option.hidden && !first) first = option
    })
    if (first) first.classList.add('is-active')
    input.setAttribute('aria-activedescendant', first ? first.id : '')
    list.hidden = false
    input.setAttribute('aria-expanded', 'true')
  }

  comboKey(event) {
    const combo = event.target.closest('[data-combo]')
    const list = combo.querySelector('[data-combo-list]')
    const options = Array.from(list.querySelectorAll('[role="option"]')).filter(option => !option.hidden)
    const index = options.findIndex(option => option.classList.contains('is-active'))
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault()
      if (list.hidden) return this.comboOpen(event)
      const next = options[(index + (event.key === 'ArrowDown' ? 1 : options.length - 1)) % options.length]
      options.forEach(option => option.classList.remove('is-active'))
      if (next) { next.classList.add('is-active'); next.scrollIntoView({ block: 'nearest' }); event.target.setAttribute('aria-activedescendant', next.id) }
    } else if (event.key === 'Enter') {
      event.preventDefault()
      if (!list.hidden && options[index]) this.comboChoose(combo, options[index])
    } else if (event.key === 'Escape') {
      event.preventDefault()
      list.hidden = true
      event.target.setAttribute('aria-expanded', 'false')
    }
  }

  comboPick(event) {
    event.preventDefault()
    this.comboChoose(event.target.closest('[data-combo]'), event.target.closest('[role="option"]'))
  }

  comboClose(event) {
    const combo = event.target.closest('[data-combo]')
    combo.querySelector('[data-combo-list]').hidden = true
    event.target.setAttribute('aria-expanded', 'false')
    // Leaving with text that matches no pick clears the text, so the field shows what is saved.
    const hidden = combo.querySelector('[data-choice]')
    event.target.value = hidden.value ? this.comboLabel(combo, hidden.value) : ''
  }

  comboChoose(combo, option) {
    const input = combo.querySelector('[data-combo-input]')
    const hidden = combo.querySelector('[data-choice]')
    hidden.value = option.dataset.value
    input.value = option.textContent
    combo.querySelectorAll('[role="option"]').forEach(other => other.setAttribute('aria-selected', other === option))
    combo.querySelector('[data-combo-list]').hidden = true
    input.setAttribute('aria-expanded', 'false')
    hidden.dispatchEvent(new Event('change', { bubbles: true }))
  }

  // A short review's visible choice: the radio writes the hidden field the form
  // sends, and a new decision starts its words afresh. The change bubbles on to refresh.
  pick(event) {
    const row = event.target.closest('[data-collection-review-target="row"]')
    const data = event.target.dataset || {}
    row.querySelector('[data-choice]').value = data.choiceValue || event.target.value
    row.querySelector('[data-detail]').value = data.pickDetail ?? ''
  }

  // A check row (lines that match, a total) is answered Reviewed or left open.
  check(event) {
    const row = event.target.closest('[data-collection-review-target="row"]')
    row.querySelector('[data-choice]').value = event.target.checked ? 'reviewed' : ''
  }

  // A value row's radios can share a decision code (Verify's printed value and
  // Other… are both a correction), so a radio matches by its code and, when it
  // carries one, by the value it writes as the detail.
  radioMatches(row, radio) {
    const data = radio.dataset || {}
    const code = data.choiceValue || radio.value
    const choice = row.querySelector('[data-choice]').value
    if (code !== choice) return false
    const detail = row.querySelector('[data-detail]').value
    if (data.pickDetail !== undefined) return detail === data.pickDetail
    const twins = Array.from(row.querySelectorAll('[data-pick][data-pick-detail]')).filter(other => ((other.dataset || {}).choiceValue || other.value) === code)
    return !twins.some(twin => detail === twin.dataset.pickDetail)
  }

  pickedRadio(row) {
    return Array.from(row.querySelectorAll('[data-pick]')).find(radio => this.radioMatches(row, radio))
  }

  // Each row's sentence for the note: the value picked, or the words typed.
  noteRows() {
    return this.rowTargets.map(row => {
      const radio = row.querySelector('[data-choice]').value ? this.pickedRadio(row) : null
      const detail = row.querySelector('[data-detail]').value.trim()
      let text = null
      const combo = row.querySelector('[data-combo-input]')
      if (row.querySelector('[data-check]')) text = row.querySelector('[data-choice]').value === 'reviewed' ? 'reviewed' : null
      else if (combo && row.querySelector('[data-choice]').value) text = row.querySelector('[data-choice]').value === 'no_match' ? (detail ? `no suitable match: ${detail}` : 'no suitable match') : `maps to ${combo.value}`
      else if (radio?.hasAttribute('data-pick-other')) text = detail || null
      else if (radio) text = detail && !(radio.dataset || {}).pickDetail ? `${radio.dataset.say}: ${detail}` : radio.dataset.say
      return { id: row.dataset.rowId || row.dataset.rowLabel, label: row.dataset.rowLabel, text }
    })
  }

  // A long review is kept in this browser while it is worked through, and
  // dropped when it is submitted or the review changes (the key carries its
  // revision). Storage can be unavailable; the review then works without it.
  saveDraft() {
    if (!this.draftKeyValue || this.restoring) return
    try {
      const rows = this.rowTargets.map(row => [row.querySelector('[data-choice]').value, row.querySelector('[data-detail]').value])
      window.localStorage.setItem(this.draftKeyValue, JSON.stringify({ rows, note: this.noteField?.value || '', owned: !!this.noteOwned, written: this.noteWritten || {} }))
    } catch (_) {}
  }

  restoreDraft() {
    if (!this.draftKeyValue) return
    let draft = null
    try { draft = JSON.parse(window.localStorage.getItem(this.draftKeyValue) || 'null') } catch (_) {}
    if (!draft || !Array.isArray(draft.rows) || draft.rows.length !== this.rowTargets.length) return
    this.restoring = true
    this.rowTargets.forEach((row, index) => {
      const [choice, detail] = draft.rows[index] || []
      row.querySelector('[data-choice]').value = choice || ''
      row.querySelector('[data-detail]').value = detail || ''
      const combo = row.querySelector('[data-combo]')
      if (combo) {
        combo.querySelector('[data-combo-input]').value = this.comboLabel(combo, choice || '')
        combo.querySelectorAll('[role="option"]').forEach(option => option.setAttribute('aria-selected', String(option.dataset.value === choice)))
      }
    })
    if (this.noteField) { this.noteField.value = draft.note || ''; this.noteOwned = !!draft.owned; this.noteWritten = draft.written || {} }
    this.restoring = false
  }

  clearDraft() {
    if (!this.draftKeyValue) return
    try { window.localStorage.removeItem(this.draftKeyValue) } catch (_) {}
  }

  // A short review's "Evidence" word opens the row's evidence under its values.
  toggleEvidence(event) {
    const evidence = event.currentTarget.closest('[data-collection-review-target="row"]').querySelector('[data-evidence]')
    evidence.hidden = !evidence.hidden
    event.currentTarget.setAttribute('aria-expanded', String(!evidence.hidden))
  }

  // A value's source icon does both at once: it opens the evidence under the
  // row and the document at that value. Closing the evidence leaves the
  // document alone.
  toggleSource(event) {
    this.toggleEvidence(event)
    const row = event.currentTarget.closest('[data-collection-review-target="row"]')
    const evidence = row.querySelector('[data-evidence]')
    if (!evidence.hidden) evidence.querySelector('a.collection-review__find')?.click()
  }

  // The rows whose sentence the person changed in the note go with the
  // submission, so their answers apply nothing on their own.
  markNoteEdits() {
    let field = this.form.querySelector('[data-note-edits]')
    if (!field) {
      field = document.createElement('input')
      field.type = 'hidden'
      field.name = 'task_action[submission_payload][__note_edits]'
      field.dataset.noteEdits = ''
      this.form.appendChild(field)
    }
    field.value = this.noteOwned ? editedRows(this.noteField.value, this.noteRows()).join(',') : ''
  }

  // Map reviews: give every undecided row its first candidate, the best match
  // the agent offered. Rows a person already decided keep their choice.
  acceptSuggested() {
    this.rowTargets.forEach(row => {
      const hidden = row.querySelector('[data-choice]')
      if (!hidden || hidden.value) return
      const radio = row.querySelector('[data-pick][data-suggested]')
      if (radio) { hidden.value = radio.value; return }
      const combo = row.querySelector('[data-combo]')
      const first = combo?.querySelector('[role="option"][data-value^="match:"]')
      if (first) this.comboChoose(combo, first)
    })
    this.refresh()
  }

  comboLabel(combo, value) {
    return combo.querySelector(`[role="option"][data-value="${CSS.escape(value)}"]`)?.textContent || ''
  }

  selectAll() {
    this.rowTargets.filter(row => !row.hidden).forEach(row => { row.querySelector('[data-select]').checked = this.allTarget.checked })
    this.refresh()
  }

  clearSelection() {
    this.rowTargets.forEach(row => { row.querySelector('[data-select]').checked = false })
    this.dismissUndo()
  }

  apply() {
    const rows = this.rowTargets.filter(row => !row.hidden && row.querySelector('[data-select]').checked)
    // Keep what each row held, so Apply can be taken back.
    this.undoState = rows.map(row => [row, row.querySelector('[data-choice]').value, row.querySelector('[data-detail]').value])
    rows.forEach(row => {
      row.querySelector('[data-choice]').value = this.bulkTarget.value
      row.querySelector('[data-detail]').value = ''
      row.querySelector('[data-select]').checked = false
    })
    this.undoCountTarget.textContent = rows.length
    this.undoStripTarget.hidden = false
    clearTimeout(this.undoTimer)
    this.undoTimer = setTimeout(() => this.dismissUndo(), 8000)
    this.filter()
  }

  undo() {
    ;(this.undoState || []).forEach(([row, choice, detail]) => {
      row.querySelector('[data-choice]').value = choice
      row.querySelector('[data-detail]').value = detail
    })
    this.dismissUndo()
  }

  dismissUndo() {
    clearTimeout(this.undoTimer)
    this.undoState = null
    if (this.hasUndoStripTarget) this.undoStripTarget.hidden = true
    this.filter()
  }

  // Shift-click selects every visible row between the last box clicked and this one.
  selectRow(event) {
    const box = event.currentTarget
    const visible = this.rowTargets.filter(row => !row.hidden)
    const index = visible.findIndex(row => row.contains(box))
    if (event.shiftKey && this.lastSelected != null) {
      const [from, to] = [this.lastSelected, index].sort((a, b) => a - b)
      visible.slice(from, to + 1).forEach(row => { row.querySelector('[data-select]').checked = box.checked })
    }
    this.lastSelected = index
  }

  // Clicking an issue narrows the list to every row with that issue; clicking it again clears.
  filterByIssue(event) {
    if (!this.hasSearchTarget) return
    const issue = event.currentTarget.textContent.trim()
    this.searchTarget.value = this.searchTarget.value === issue ? '' : issue
    this.filter()
  }

  validate(event) {
    // A value review can be answered with the note alone: a row left undecided
    // becomes follow-up, unless it writes to a document field.
    const noteOnly = this.noteField && this.noteField.value.trim() !== ''
    const row = this.rowTargets.find(row => !this.complete(row) && !(noteOnly && !row.querySelector('[data-choice]').value && !row.hasAttribute('data-required')))
    if (!row) {
      this.submitting = true
      if (this.noteField) this.markNoteEdits()
      this.clearDraft()
      return
    }
    event.preventDefault()
    event.stopImmediatePropagation()
    if (this.hasFilterTarget) this.filterTarget.checked = false
    if (this.hasSearchTarget) this.searchTarget.value = ''
    this.filter()
    const field = row.querySelector('[data-choice]').value ? row.querySelector('[data-detail]') : (row.querySelector('[data-combo-input]') || row.querySelector('[data-pick]') || row.querySelector('[data-choice]'))
    // An undecided row that a note may answer says so; a required one, or a picked one missing its detail, does not.
    const noteMay = this.noteField && !row.querySelector('[data-choice]').value && !row.hasAttribute('data-required')
    field.setCustomValidity(noteMay ? 'Pick an answer, or write one in the note.' : 'Complete this review item before submitting.')
    field.reportValidity()
    field.addEventListener('input', () => field.setCustomValidity(''), { once: true })
    field.addEventListener('change', () => field.setCustomValidity(''), { once: true })
  }
}
